import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface TOCSection {
    id: string;
    title: string;
}

interface TableOfContentsProps {
    sections: TOCSection[];
}

export function TableOfContents({ sections }: TableOfContentsProps) {
    const [activeSection, setActiveSection] = useState<string>('');
    const { t } = useTranslation('common');

    useEffect(() => {
        const observerOptions: IntersectionObserverInit = {
            rootMargin: '-80px 0px -70% 0px',
            threshold: 0
        };

        const observerCallback: IntersectionObserverCallback = (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    setActiveSection(entry.target.id);
                }
            });
        };

        const observer = new IntersectionObserver(observerCallback, observerOptions);

        sections.forEach(({ id }) => {
            const element = document.getElementById(id);
            if (element) {
                observer.observe(element);
            }
        });

        return () => observer.disconnect();
    }, [sections]);

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
        e.preventDefault();
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
            // Update URL without scrolling
            window.history.pushState(null, '', `#${id}`);
        }
    };

    if (sections.length === 0) return null;

    return (
        <nav className="toc" aria-label={t('tableOfContents') as string}>
            <div className="toc-title">{t('contents') as string}</div>
            {sections.map(({ id, title }) => (
                <a
                    key={id}
                    href={`#${id}`}
                    className={`toc-link ${activeSection === id ? 'active' : ''}`}
                    onClick={(e) => handleClick(e, id)}
                >
                    {title}
                </a>
            ))}
        </nav>
    );
}
